import type {
  AdminPlan,
  AdminPlanStripeProductUpdateRequest,
  AdminStripeConfig,
  AdminStripePrice,
  AdminStripeSubscriptionStateClearResponse,
  AdminUserPlanUpdateRequest,
  AdminUserSummary,
  AppSummary,
  BillingAccount,
  BillingPlansResponse,
  CreateAppRequest,
  CreatedReviewLink,
  CreateReviewLinkRequest,
  CreateStoreScreenshotUploadSessionRequest,
  CreateStoreScreenshotCommentRequest,
  ResolvedReviewLink,
  ReviewLinkSummary,
  StripeCheckoutSessionRequest,
  StripeCheckoutSessionResponse,
  StripeCustomerPortalSessionRequest,
  StripeCustomerPortalSessionResponse,
  StripePriceSyncResponse,
  StripeSubscriptionManagementResponse,
  StripeSubscriptionPlanChangeRequest,
  StoreAppSearchResult,
  StoreScreenshotComment,
  StoreScreenshot,
  StoreScreenshotRefreshResponse,
  StoreScreenshotUploadSession,
  MergeAnonymousReviewerRequest,
  UpdateCommentSettingsRequest,
  UpdateAppStoreMetadataRequest,
  UpdateCommentIdentityRequest,
  UpdateStoreScreenshotDescriptionRequest,
  UpdateUserLanguagesRequest,
  UserProfile
} from "../types";

export interface ApiContext {
  getIdToken: () => Promise<string | null>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function isPaymentRequiredError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 402;
}

export async function getMe(context: ApiContext): Promise<UserProfile> {
  return authorizedJson<UserProfile>("/api/v1/me", context);
}

export async function updateMyLanguages(request: UpdateUserLanguagesRequest, context: ApiContext): Promise<UserProfile> {
  return authorizedJson<UserProfile>("/api/v1/me/languages", context, {
    method: "PATCH",
    body: request
  });
}

export async function updateMyCommentIdentity(request: UpdateCommentIdentityRequest, context: ApiContext): Promise<UserProfile> {
  return authorizedJson<UserProfile>("/api/v1/me/comment-identity", context, {
    method: "PATCH",
    body: request
  });
}

export async function mergeAnonymousReviewer(request: MergeAnonymousReviewerRequest, context: ApiContext): Promise<UserProfile> {
  return authorizedJson<UserProfile>("/api/v1/me/merge-anonymous-reviewer", context, {
    method: "POST",
    body: request
  });
}

export async function getBillingAccount(context: ApiContext): Promise<BillingAccount> {
  return authorizedJson<BillingAccount>("/api/v1/billing/account", context);
}

export async function listBillingPlans(context: ApiContext): Promise<BillingPlansResponse> {
  return authorizedJson<BillingPlansResponse>("/api/v1/billing/plans", context);
}

export async function createStripeCheckoutSession(
  request: StripeCheckoutSessionRequest,
  context: ApiContext
): Promise<StripeCheckoutSessionResponse> {
  return authorizedJson<StripeCheckoutSessionResponse>("/api/v1/billing/stripe/checkout-session", context, {
    method: "POST",
    body: request
  });
}

export async function createStripeCustomerPortalSession(
  request: StripeCustomerPortalSessionRequest,
  context: ApiContext
): Promise<StripeCustomerPortalSessionResponse> {
  return authorizedJson<StripeCustomerPortalSessionResponse>("/api/v1/billing/stripe/customer-portal-session", context, {
    method: "POST",
    body: request
  });
}

export async function changeStripeSubscriptionPlan(
  request: StripeSubscriptionPlanChangeRequest,
  context: ApiContext
): Promise<StripeSubscriptionManagementResponse> {
  return authorizedJson<StripeSubscriptionManagementResponse>("/api/v1/billing/stripe/subscription/change-plan", context, {
    method: "POST",
    body: request
  });
}

export async function cancelStripeSubscription(context: ApiContext): Promise<StripeSubscriptionManagementResponse> {
  return authorizedJson<StripeSubscriptionManagementResponse>("/api/v1/billing/stripe/subscription/cancel", context, {
    method: "POST"
  });
}

export async function fetchAdminUsers(context: ApiContext): Promise<AdminUserSummary[]> {
  return authorizedJson<AdminUserSummary[]>("/api/v1/admin/users", context);
}

export async function updateAdminUserPlan(
  userId: string,
  request: AdminUserPlanUpdateRequest,
  context: ApiContext
): Promise<AdminUserSummary> {
  return authorizedJson<AdminUserSummary>(`/api/v1/admin/users/${encodeURIComponent(userId)}/plan`, context, {
    method: "PATCH",
    body: request
  });
}

export async function fetchAdminPlans(context: ApiContext): Promise<AdminPlan[]> {
  return authorizedJson<AdminPlan[]>("/api/v1/admin/plans", context);
}

export async function updateAdminPlanStripeProduct(
  planCode: string,
  request: AdminPlanStripeProductUpdateRequest,
  context: ApiContext
): Promise<AdminPlan> {
  return authorizedJson<AdminPlan>(`/api/v1/admin/plans/${encodeURIComponent(planCode)}/stripe-product`, context, {
    method: "PATCH",
    body: request
  });
}

export async function fetchAdminStripeConfig(context: ApiContext): Promise<AdminStripeConfig> {
  return authorizedJson<AdminStripeConfig>("/api/v1/admin/billing/stripe/config", context);
}

export async function fetchAdminStripePrices(context: ApiContext): Promise<AdminStripePrice[]> {
  return authorizedJson<AdminStripePrice[]>("/api/v1/admin/billing/stripe/prices", context);
}

export async function syncAdminStripePrices(context: ApiContext): Promise<StripePriceSyncResponse> {
  return authorizedJson<StripePriceSyncResponse>("/api/v1/admin/billing/stripe/prices/sync", context, {
    method: "POST"
  });
}

export async function clearAdminStripeSubscriptionState(context: ApiContext): Promise<AdminStripeSubscriptionStateClearResponse> {
  return authorizedJson<AdminStripeSubscriptionStateClearResponse>("/api/v1/admin/billing/stripe/subscription-state/clear", context, {
    method: "POST"
  });
}

export async function listApps(context: ApiContext): Promise<AppSummary[]> {
  return authorizedJson<AppSummary[]>("/api/v1/apps", context);
}

export async function getApp(appId: string, context: ApiContext): Promise<AppSummary> {
  return authorizedJson<AppSummary>(`/api/v1/apps/${encodeURIComponent(appId)}`, context);
}

export async function saveApp(appId: string, context: ApiContext): Promise<AppSummary> {
  return authorizedJson<AppSummary>(`/api/v1/apps/${encodeURIComponent(appId)}/save`, context, {
    method: "POST"
  });
}

export async function removeSavedApp(appId: string, context: ApiContext): Promise<void> {
  return authorizedJson<void>(`/api/v1/apps/${encodeURIComponent(appId)}/save`, context, {
    method: "DELETE"
  });
}

export async function deleteApp(appId: string, name: string, context: ApiContext): Promise<void> {
  return authorizedJson<void>(`/api/v1/apps/${encodeURIComponent(appId)}`, context, {
    method: "DELETE",
    body: { name }
  });
}

export async function createApp(request: CreateAppRequest, context: ApiContext): Promise<AppSummary> {
  return authorizedJson<AppSummary>("/api/v1/apps", context, {
    method: "POST",
    body: request
  });
}

export async function createReviewLink(
  appId: string,
  request: CreateReviewLinkRequest,
  context: ApiContext
): Promise<CreatedReviewLink> {
  return authorizedJson<CreatedReviewLink>(`/api/v1/apps/${encodeURIComponent(appId)}/review-links`, context, {
    method: "POST",
    body: request
  });
}

export async function resolveReviewLink(code: string, context: ApiContext): Promise<ResolvedReviewLink> {
  return authorizedJson<ResolvedReviewLink>(`/api/v1/review-links/${encodeURIComponent(code)}`, context);
}

export async function listReviewLinks(appId: string, context: ApiContext): Promise<ReviewLinkSummary[]> {
  return authorizedJson<ReviewLinkSummary[]>(`/api/v1/apps/${encodeURIComponent(appId)}/review-links`, context);
}

export async function deleteReviewLink(appId: string, code: string, context: ApiContext): Promise<void> {
  return authorizedJson<void>(
    `/api/v1/apps/${encodeURIComponent(appId)}/review-links/${encodeURIComponent(code)}`,
    context,
    { method: "DELETE" }
  );
}

export async function updateAppStoreMetadata(
  appId: string,
  request: UpdateAppStoreMetadataRequest,
  context: ApiContext
): Promise<AppSummary> {
  return authorizedJson<AppSummary>(`/api/v1/apps/${encodeURIComponent(appId)}/store-metadata`, context, {
    method: "PATCH",
    body: request
  });
}

export async function updateCommentSettings(
  appId: string,
  request: UpdateCommentSettingsRequest,
  context: ApiContext
): Promise<AppSummary> {
  return authorizedJson<AppSummary>(`/api/v1/apps/${encodeURIComponent(appId)}/comment-settings`, context, {
    method: "PATCH",
    body: request
  });
}

export async function listReferenceLocales(appId: string, context: ApiContext): Promise<string[]> {
  return authorizedJson<string[]>(`/api/v1/apps/${encodeURIComponent(appId)}/reference-locales`, context);
}

export async function updateReferenceLocales(appId: string, referenceLocales: string[], context: ApiContext): Promise<string[]> {
  return authorizedJson<string[]>(`/api/v1/apps/${encodeURIComponent(appId)}/reference-locales`, context, {
    method: "PATCH",
    body: { referenceLocales }
  });
}

export async function searchAppStore(query: string, context: ApiContext, signal?: AbortSignal): Promise<StoreAppSearchResult[]> {
  return authorizedJson<StoreAppSearchResult[]>(
    `/api/v1/store/ios/search?query=${encodeURIComponent(query)}`,
    context,
    { signal }
  );
}

export async function searchGooglePlay(query: string, context: ApiContext, signal?: AbortSignal): Promise<StoreAppSearchResult[]> {
  return authorizedJson<StoreAppSearchResult[]>(
    `/api/v1/store/android/search?query=${encodeURIComponent(query)}`,
    context,
    { signal }
  );
}

export async function listStoreScreenshots(appId: string, context: ApiContext): Promise<StoreScreenshot[]> {
  return authorizedJson<StoreScreenshot[]>(`/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots`, context);
}

export async function refreshStoreScreenshots(appId: string, locales: string[], context: ApiContext, platform?: "IOS" | "ANDROID"): Promise<StoreScreenshotRefreshResponse> {
  return authorizedJson<StoreScreenshotRefreshResponse>(`/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/refresh`, context, {
    method: "POST",
    body: { locales, platform }
  });
}

export interface UploadStoreScreenshotAssignment {
  fileIndex: number;
  position: number;
  targetScreenshotId?: string | null;
}

export interface UploadStoreScreenshotProgress {
  fileIndex: number;
  loadedBytes: number;
  totalBytes: number;
  percent: number;
}

export async function uploadStoreScreenshots(
  appId: string,
  platform: "IOS" | "ANDROID",
  localeTag: string,
  files: File[],
  items: UploadStoreScreenshotAssignment[],
  context: ApiContext,
  onProgress?: (progress: UploadStoreScreenshotProgress) => void
): Promise<StoreScreenshot[]> {
  const request: CreateStoreScreenshotUploadSessionRequest = {
    platform,
    localeTag,
    items: items.map((item) => {
      const file = files[item.fileIndex];
      if (!file) {
        throw new Error(`Upload file index ${item.fileIndex} is invalid.`);
      }
      return {
        fileIndex: item.fileIndex,
        fileName: file.name,
        contentType: file.type || null,
        sizeBytes: file.size,
        position: item.position,
        targetScreenshotId: item.targetScreenshotId ?? null
      };
    })
  };

  const session = await authorizedJson<StoreScreenshotUploadSession>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/upload-sessions`,
    context,
    {
      method: "POST",
      body: request
    }
  );

  try {
    for (const uploadItem of session.items) {
      const file = files[uploadItem.fileIndex];
      if (!file) {
        throw new Error(`Upload file index ${uploadItem.fileIndex} is invalid.`);
      }
      await uploadFileToSignedUrl(file, uploadItem, onProgress);
    }

    return await authorizedJson<StoreScreenshot[]>(
      `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/upload-sessions/${encodeURIComponent(session.id)}/commit`,
      context,
      {
        method: "POST"
      }
    );
  } catch (error) {
    try {
      await authorizedJson<{ status: string }>(
        `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/upload-sessions/${encodeURIComponent(session.id)}/cancel`,
        context,
        {
          method: "POST"
        }
      );
    } catch {
      // Preserve the original upload/commit error; expiry cleanup will release the reservation if cancel fails.
    }
    throw error;
  }
}

function uploadFileToSignedUrl(
  file: File,
  uploadItem: StoreScreenshotUploadSession["items"][number],
  onProgress?: (progress: UploadStoreScreenshotProgress) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", uploadItem.uploadUrl);
    Object.entries(uploadItem.headers ?? {}).forEach(([name, value]) => {
      request.setRequestHeader(name, value);
    });
    request.upload.onprogress = (event) => {
      const totalBytes = event.lengthComputable ? event.total : file.size;
      const loadedBytes = event.loaded;
      onProgress?.({
        fileIndex: uploadItem.fileIndex,
        loadedBytes,
        totalBytes,
        percent: totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 0
      });
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress?.({
          fileIndex: uploadItem.fileIndex,
          loadedBytes: file.size,
          totalBytes: file.size,
          percent: 100
        });
        resolve();
        return;
      }
      reject(new Error(`Direct upload failed with ${request.status}${request.statusText ? ` ${request.statusText}` : ""}.`));
    };
    request.onerror = () => reject(new Error("Direct upload failed. Check the R2 bucket CORS configuration and try again."));
    request.onabort = () => reject(new Error("Direct upload was cancelled."));
    request.send(file);
  });
}

export async function deleteUploadedStoreScreenshot(
  appId: string,
  screenshotId: string,
  context: ApiContext
): Promise<StoreScreenshot[]> {
  return authorizedJson<StoreScreenshot[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/upload`,
    context,
    {
      method: "DELETE"
    }
  );
}

export async function removeStoreScreenshot(
  appId: string,
  screenshotId: string,
  context: ApiContext
): Promise<StoreScreenshot[]> {
  return authorizedJson<StoreScreenshot[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/store`,
    context,
    {
      method: "DELETE"
    }
  );
}

export async function stackStoreScreenshot(
  appId: string,
  screenshotId: string,
  targetScreenshotId: string,
  context: ApiContext
): Promise<StoreScreenshot[]> {
  return authorizedJson<StoreScreenshot[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/stack`,
    context,
    {
      method: "POST",
      body: { targetScreenshotId }
    }
  );
}

export async function unstackStoreScreenshot(
  appId: string,
  screenshotId: string,
  context: ApiContext
): Promise<StoreScreenshot[]> {
  return authorizedJson<StoreScreenshot[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/unstack`,
    context,
    {
      method: "POST"
    }
  );
}

export async function updateStoreScreenshotDescription(
  appId: string,
  screenshotId: string,
  request: UpdateStoreScreenshotDescriptionRequest,
  context: ApiContext
): Promise<StoreScreenshot[]> {
  return authorizedJson<StoreScreenshot[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/description`,
    context,
    {
      method: "PATCH",
      body: request
    }
  );
}

export async function listScreenshotComments(
  appId: string,
  screenshotId: string,
  context: ApiContext
): Promise<StoreScreenshotComment[]> {
  return authorizedJson<StoreScreenshotComment[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/comments`,
    context
  );
}

export async function createScreenshotComment(
  appId: string,
  screenshotId: string,
  request: CreateStoreScreenshotCommentRequest,
  context: ApiContext
): Promise<StoreScreenshotComment[]> {
  return authorizedJson<StoreScreenshotComment[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/comments`,
    context,
    {
      method: "POST",
      body: request
    }
  );
}

export async function deleteScreenshotComment(
  appId: string,
  screenshotId: string,
  commentId: string,
  context: ApiContext
): Promise<StoreScreenshotComment[]> {
  return authorizedJson<StoreScreenshotComment[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/comments/${encodeURIComponent(commentId)}`,
    context,
    {
      method: "DELETE"
    }
  );
}

export async function resolveScreenshotComment(
  appId: string,
  screenshotId: string,
  commentId: string,
  context: ApiContext
): Promise<StoreScreenshotComment[]> {
  return authorizedJson<StoreScreenshotComment[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/comments/${encodeURIComponent(commentId)}/resolve`,
    context,
    {
      method: "PATCH"
    }
  );
}

export async function unresolveScreenshotComment(
  appId: string,
  screenshotId: string,
  commentId: string,
  context: ApiContext
): Promise<StoreScreenshotComment[]> {
  return authorizedJson<StoreScreenshotComment[]>(
    `/api/v1/apps/${encodeURIComponent(appId)}/store-screenshots/${encodeURIComponent(screenshotId)}/comments/${encodeURIComponent(commentId)}/unresolve`,
    context,
    {
      method: "PATCH"
    }
  );
}

async function authorizedJson<T>(
  path: string,
  context: ApiContext,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {}
): Promise<T> {
  const token = await context.getIdToken();
  const headers: Record<string, string> = {
    Accept: "application/json"
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(path, {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal
  });

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorMessage(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function readErrorMessage(response: Response): Promise<string> {
  if (response.status === 401) {
    return "Unauthorized. Sign in and try again.";
  }

  const fallback = `Request failed with ${response.status}${response.statusText ? ` ${response.statusText}` : ""}`;
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    try {
      const payload = (await response.json()) as { message?: unknown; error?: unknown };
      const message = typeof payload.message === "string" ? payload.message.trim() : "";
      if (isDisplayableErrorMessage(message, response.status)) {
        return message;
      }

      const error = typeof payload.error === "string" ? payload.error.trim() : "";
      return isDisplayableErrorMessage(error, response.status) ? `${fallback}: ${error}` : fallback;
    } catch {
      return fallback;
    }
  }

  const text = (await response.text()).trim();
  if (!isDisplayableErrorMessage(text, response.status)) {
    return fallback;
  }
  return text;
}

function isDisplayableErrorMessage(value: string, status: number): boolean {
  return Boolean(value) && status < 500 && value.length <= 240 && !looksLikeInternalError(value);
}

function looksLikeInternalError(value: string): boolean {
  return (
    looksLikeStackTrace(value) ||
    value.includes("PreparedStatementCallback") ||
    value.includes("BadSqlGrammarException") ||
    value.includes("SQLException") ||
    value.includes("Servlet.service()") ||
    value.includes("org.springframework") ||
    value.includes("bad SQL grammar")
  );
}

function looksLikeStackTrace(value: string): boolean {
  return value.includes("\n\tat ") || value.includes("Exception:") || value.includes("Servlet.service()");
}
